/**
 * Evidence image component with loading and error states
 *
 * Displays images from blob URLs with proper loading states,
 * error handling, and accessibility attributes.
 */

import { useEffect, useState } from 'react';

import { blobUrlRegistry } from '@/utils/blobUrl.util';
import { isBlobUrl } from '@/utils/imageDetection.util';

interface EvidenceImageProps {
  /** Image source (blob URL or regular URL) */
  src: unknown;
  /** Alt text for accessibility */
  altText?: string;
  /** CSS class for container */
  containerClassName?: string;
  /** CSS class for image element */
  imageClassName?: string;
  /** Maximum height (CSS value) */
  maxHeight?: string;
  /** Registry key for blob URL cleanup */
  registryKey?: string;
}

/**
 * Evidence image component with loading and error states
 *
 * Features:
 * - Loading skeleton while image loads
 * - Error state with fallback UI
 * - Automatic blob URL cleanup on unmount
 * - Responsive image container
 * - Proper accessibility attributes
 *
 * @example
 * <EvidenceImage
 *   src={blobUrl}
 *   altText="Evidence screenshot"
 *   registryKey="evidence.png"
 * />
 */
export function EvidenceImage({
  src,
  altText = 'Evidence',
  containerClassName = 'flex justify-center p-4',
  imageClassName = 'max-w-full rounded-md border border-chariot-stroke',
  maxHeight = '80vh',
  registryKey,
}: EvidenceImageProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // ✅ Always call hooks at top level, before any returns
  const imageUrl = isBlobUrl(src) ? (src as string) : null;

  // Register blob URL for cleanup when component unmounts (runs even if imageUrl is null)
  useEffect(() => {
    if (imageUrl && isBlobUrl(imageUrl)) {
      const cleanup = blobUrlRegistry.register(
        imageUrl,
        registryKey || imageUrl
      );
      return cleanup; // Cleanup function called on unmount
    }
  }, [imageUrl, registryKey]);

  // ✅ NOW it's safe to return early
  if (!imageUrl) {
    return null;
  }

  // Handle image load
  const handleLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  // Handle image error
  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
    console.error('Failed to load image:', imageUrl);
  };

  // Error state
  if (hasError) {
    return (
      <div className={containerClassName}>
        <div className="border-chariot-stroke bg-layer1 flex flex-col items-center justify-center rounded-md border p-8 text-center">
          <svg
            className="text-chariot-text-secondary mb-4 size-16"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h3 className="text-chariot-text-primary mb-2 text-lg font-medium">
            Failed to Load Image
          </h3>
          <p className="text-chariot-text-secondary max-w-md text-sm">
            The image could not be displayed. This may be due to a corrupted
            file, network error, or unsupported format.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={containerClassName}>
      {/* Loading skeleton */}
      {isLoading && (
        <div
          className="border-chariot-stroke bg-layer1 animate-pulse rounded-md border"
          style={{ maxHeight, width: '100%', maxWidth: '800px' }}
          aria-label="Loading image"
        >
          <div className="flex h-64 items-center justify-center">
            <svg
              className="text-chariot-text-secondary size-12 animate-spin"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
        </div>
      )}

      {/* Image - eager loading to avoid lazy-load + display:none race condition */}
      <img
        src={imageUrl}
        alt={altText}
        className={imageClassName}
        style={{
          maxHeight,
          display: isLoading ? 'none' : 'block',
        }}
        onLoad={handleLoad}
        onError={handleError}
      />
    </div>
  );
}
